import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { UsageService } from './usage.service';
import { UserService } from './user.service';
import { StateCleanupService } from './state-cleanup.service';

export const OrganisationFeatures = {
  EXERCISE_MODE: 'EXERCISE_MODE',
  CONTENT_GENERATOR: 'CONTENT_GENERATOR',
  ANALYTICS: 'ANALYTICS',
  DOZENT: 'DOZENT',
  // Opt-in: when enabled, "KI-Agenten" expands into Tutoren + Placements
  // sub-pages so admins can bulk-place tutors across many courses. When
  // disabled, KI-Agenten is a flat link to the tutor list (legacy).
  TUTOR_PLACEMENTS: 'TUTOR_PLACEMENTS',
  // Opt-in: shows the master "Moodle" sidebar entry with the
  // courses/learners overview + per-course dozent assignment matrix.
  // Backend mirrors this in JWT payload.moodle_dashboard_enabled.
  MOODLE_DASHBOARD: 'MOODLE_DASHBOARD',
  // Opt-in: shows the "Curriculum" sidebar entry — study-program tree,
  // Moodle-category / course-bucket adoption, curriculum document import,
  // and rule-based learner assignment. super_admins always see it.
  CURRICULUM: 'CURRICULUM',
  // Opt-in: the Learning Engine extension — learners with their own account
  // and enrolment, learning paths built from uploaded content, the learner
  // workspace. Without it a client keeps the LMS-bound behaviour unchanged.
  // See features/prd/learning-engine.md.
  LEARNING_ENGINE: 'LEARNING_ENGINE',
  // Opt-in: the portal assistant (in-house agent, panel at the top right) and
  // connecting one's own AI agent over MCP (Einstellungen › Portal-Assistent).
  // al-tutor and al-user-management read it from clients.features.
  // See features/docs/technical/portal-agent-native.md.
  PORTAL_AGENT: 'PORTAL_AGENT',
};

/** One row from `GET /admin/clients/{id}/template-access`. Only
 *  non-public templates are returned — public ones are visible to
 *  every client unconditionally and don't need an explicit grant. */
export interface ClientTemplateAccess {
  template_id: string;
  name: string;
  description?: string | null;
  access_level?: string | null;
  granted: boolean;
}

export interface ClientsResponse {
  client_details: Organisation;
  usage_summary: {
    api_usage: any;
    bot_usage: any;
    document_usage: any;
    file_usage: any;
    url_usage: any;
    moodle_course_usage: any;
  };
}

export interface Organisation {
  id: string; // UUID
  company_name: string;
  features: string[]; // e.g. ['EXERCISE_MODE', 'CONTENT_GENERATOR', 'ANALYTICS']
  /** SCORM-Export im Creator (al-user-management Migration 085); fehlt = an. */
  creator_export_allowed?: boolean;
  phone?: string;
  street?: string;
  number?: string;
  email?: string;
  city?: string;
  zip?: string;
  region?: string;
  country?: string;
  /** ISO-3166-1 alpha-2, migration 027. Authoritative for the Stripe sync;
   * `country` stays the display name. */
  country_code?: string;
  legal_form?: string;
  trial_used?: boolean;
  /** National tax number (Steuernummer) - not sent to Stripe. */
  tax_number?: string;
  /** EU VAT ID (USt-IdNr.), pushed to Stripe as a customer tax_id. */
  vat_id?: string;
  /** Self-declared. Stored only - does not change the invoice yet. */
  reverse_charge_requested?: boolean;
  current_plan?: string; // UUID, ForeignKey to plans
  plan_name?: string;
  /** Anzeigename eines Vertragsplans (Migration 081); null bei Standardplaenen. */
  plan_display_name?: string | null;
  /** Kunde hat einen individuellen Vertrag — keine Stripe-Wege im Portal. */
  is_contract?: boolean;
  subscription_status: string; // default 'active'
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  subscription_end_date?: string;
  subscription_start_date?: string;
  /** Last successful portal login by any user of the organisation
   *  (super-admin list only). Impersonation doesn't count. */
  last_login_at?: string | null;
  allowed_domains?: string[];
  avv_accepted?: boolean;
  avv_accepted_at?: string | null;
  avv_version?: string | null;
  avv_customer_name?: string | null;
  avv_customer_address?: string | null;
  avv_customer_street?: string | null;
  avv_customer_number?: string | null;
  avv_customer_zip?: string | null;
  avv_customer_city?: string | null;
  avv_customer_country?: string | null;
  avv_accepted_by_email?: string | null;
  created_at?: Date;
  createdAt: Date;
  updatedAt: Date;
  cycle_end_date?: string;
  cycle_start_date?: string;
}

/** `GET /clients/change-plan/{id}/preview`. Amounts in cents. */
export interface PlanChangePreview {
  available: boolean;
  current_plan?: string;
  current_plan_id?: string;
  /** What the running subscription charges per interval today. */
  current_amount?: number;
  /** 'inclusive' = the old price already contains VAT. */
  current_tax_mode?: 'inclusive' | 'exclusive';
  target_plan?: string;
  currency?: string;
  interval?: 'month' | 'year';
  recurring_net?: number;
  recurring_tax?: number;
  recurring_gross?: number;
  tax_percent?: number;
  /** Prorated difference Stripe charges right away. */
  due_now?: number;
  due_now_net?: number;
  due_now_tax?: number;
  /** Unix seconds — the next regular renewal, unchanged by the upgrade. */
  next_billing_at?: number;
  /** Unix seconds the proration was calculated for. Sent back with the
   *  upgrade so Stripe charges exactly `due_now`. */
  proration_date?: number;
  /** How the customer is taxed — decides the wording of the VAT lines. */
  tax_treatment?: 'domestic' | 'reverse_charge' | 'eu_missing_vat_id' | 'non_eu';
  payment_method?: { type?: string; brand?: string; last4?: string } | null;
}

export interface ClientTokenUsageSummary {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_runs: number;
  llm_runs: number;
  trace_count: number;
  thread_count: number;
  root_run_count: number;
  error_run_count: number;
  prompt_cost: number;
  completion_cost: number;
  total_cost: number;
  currency: string;
  avg_input_tokens_per_run: number;
  avg_output_tokens_per_run: number;
  avg_total_tokens_per_run: number;
}

export interface ClientTokenUsageRunBreakdown {
  run_name: string;
  run_type: string;
  run_count: number;
  error_run_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  avg_input_tokens_per_run: number;
  avg_output_tokens_per_run: number;
  avg_total_tokens_per_run: number;
  prompt_cost: number;
  completion_cost: number;
  total_cost: number;
}

export interface ClientTokenUsageDaily {
  date: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_runs: number;
  llm_runs: number;
  trace_count: number;
  thread_count: number;
  total_cost: number;
  sync_status: string;
  synced_at?: string | null;
}

export interface ClientRuntimeTokenUsage {
  source: string;
  scope: string;
  rows: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  token_quota_limit: number;
  remaining_token_quota: number;
  updated_at?: string | null;
}

export interface ClientTokenUsageResponse {
  client_id: string;
  /** Erstes konfiguriertes Projekt — nur fuer die Anzeige. */
  project_name: string;
  /** Alle Projekte, ueber die summiert wurde. */
  project_names?: string[];
  period: {
    key: string;
    start: string;
    end: string;
    start_date: string;
    end_date: string;
  };
  summary: ClientTokenUsageSummary;
  runtime_usage?: ClientRuntimeTokenUsage;
  run_breakdown: ClientTokenUsageRunBreakdown[];
  daily: ClientTokenUsageDaily[];
  sync: {
    last_synced_at?: string | null;
    days_available: number;
    interval: {
      type: string;
      default_days: number;
      window_hours: number;
      batch_sleep_seconds: number;
    };
  };
}

export interface ClientQuotaAuditEntry {
  id: string;
  client_id: string;
  api_usage_id?: string | null;
  plan_id?: string | null;
  plan_endpoint_id?: string | null;
  changed_by_user_id?: string | null;
  changed_by_email?: string | null;
  changed_by_name?: string | null;
  old_token_quota_limit: number;
  new_token_quota_limit: number;
  old_remaining_token_quota: number;
  new_remaining_token_quota: number;
  token_usage_count: number;
  note?: string | null;
  created_at?: string | null;
}

export interface ClientQuotaRow {
  api_usage_id: string;
  plan_endpoint_id: string;
  endpoint_id?: string | null;
  endpoint_name?: string | null;
  endpoint_url?: string | null;
  usage_period: string;
  plan_token_limit: number;
  plan_request_quota: number;
  plan_rate_limit: number;
  enforce_token_quota: boolean;
  active_token_quota_limit: number;
  token_usage_count: number;
  remaining_token_quota: number;
  calculated_remaining_token_quota: number;
  updated_at?: string | null;
}

export interface AdminCreditPackage {
  id: string;
  purchase_source: 'manual' | 'auto_recharge' | 'admin_grant' | string;
  eur_amount: number;
  credits_granted: number;
  credits_remaining: number;
  purchased_at?: string | null;
  expires_at?: string | null;
  active: boolean;
  granted_by_email?: string | null;
  grant_note?: string | null;
  customer_message?: string | null;
}

/** Credit view of one client for super admins (`GET /admin/clients/{id}/credits`). */
export interface AdminClientCredits {
  client_id: string;
  plan?: { id: string; name: string } | null;
  quota_basis: 'tokens' | 'credits';
  /** null = the plan sets no monthly credit budget (unlimited). */
  monthly_included_credits: number | null;
  period_start?: string | null;
  used_credits: number;
  plan_remaining_credits: number | null;
  bought_remaining_credits: number;
  gifted_remaining_credits: number;
  /** -1 = unlimited, null = unknown. */
  available_credits: number | null;
  max_gift_credits: number;
  packages: AdminCreditPackage[];
}

export interface AdminCreditGiftPayload {
  credits: number;
  request_id: string;
  note: string;
  customer_message?: string;
  notify_customer: boolean;
}

export interface ClientQuotaDetails {
  client_id: string;
  company_name?: string | null;
  plan: {
    id?: string | null;
    name?: string | null;
  };
  quota_rows: ClientQuotaRow[];
  audit: ClientQuotaAuditEntry[];
}

// export interface Payment {
//   amount: number;
//   client_id: string;
//   created_at?: string;
//   id: string;
//   payment_provider?: string;
//   payment_status?: string;
//   updated_at?: string;
// }

export interface Invoice {
  amount_paid: number;
  paid: boolean;
  total: number;
  created: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
  number: string;
  currency: string;
}

/** What Stripe charges at the next renewal (GET /clients/payment-method). */
export interface PaymentMethodInfo {
  has_customer: boolean;
  payment_method?: {
    id: string;
    type: string; // 'card' | 'sepa_debit' | …
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
    expired?: boolean;
    expiring_soon?: boolean;
    bank_code?: string;
  } | null;
  /** Where the default comes from: 'subscription' | 'customer' | 'legacy_source' | null */
  source?: string | null;
  /** Payment methods on file — >0 with no default means Stripe will NOT use them. */
  attached_count?: number;
  subscription_status?: string | null;
  collection_method?: string | null;
  /** Set after Stripe's last failed attempt: service stops at this time (ISO). */
  payment_grace_until?: string | null;
  /** super_admin only */
  stripe_dashboard?: { customer_url: string; subscription_url: string | null; account_id: string; live: boolean };
}

export interface AvvStatus {
  version: string;
  accepted: boolean;
  accepted_at?: string | null;
  accepted_by_email?: string | null;
  customer_name: string;
  customer_address: string;
  customer_street: string;
  customer_number: string;
  customer_zip: string;
  customer_city: string;
  customer_country?: string | null;
  preview_markdown: string;
}

export interface AvvAcceptPayload {
  customer_name: string;
  customer_street: string;
  customer_number: string;
  customer_zip: string;
  customer_city: string;
  customer_country?: string | null;
  accepted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  organisation$ = new BehaviorSubject<Organisation | undefined>(undefined);
  organisations$ = new BehaviorSubject<Organisation[]>([]);
  invoices$ = new BehaviorSubject<Invoice[]>([]);

  constructor(
    protected http: HttpClient,
    protected userService: UserService,
    protected authService: AuthService,
    protected usageService: UsageService,
    protected stateCleanupService: StateCleanupService
  ) {
    if (this.organisation == undefined) {
      this.getOrganisation().subscribe();
    }
    this.stateCleanupService.register(() => this.resetState());
  }

  getOrganisation() {
    const id = this.userService.me$.value?.client_id;
    return this.http
      .get<{ data: ClientsResponse }>(`${environment.userBackend}/clients`)
      .pipe(
        map((data) => data.data),
        tap((org) => {
          this.organisation$.next(org.client_details);
          this.usageService.usage$.next(org.usage_summary);
        })
      );
  }

  /** `silent`: the caller shows a failure itself, skip the generic error toast. */
  updateMyOrganisation(org: Partial<Organisation>, silent = false) {
    return this.http
      .patch<{ data: Organisation }>(
        `${environment.userBackend}/clients`,
        org,
        silent ? { headers: { 'X-Suppress-Error-Toast': '1' } } : {}
      )
      .pipe(
        map((data) => data.data),
        tap((patchedOrga) => {
          this.organisation$.next(patchedOrga);
        })
      );
  }

  updateOrganisation(org: Partial<Organisation>) {
    return this.http
      .patch<{ data: Organisation }>(
        `${environment.userBackend}/admin/clients/${org.id}`,
        org
      )
      .pipe(
        map((data) => data.data),
        tap((patchedOrga) => {
          this.organisations$.next(
            this.organisations$.value.map((o) =>
              o.id === patchedOrga.id ? patchedOrga : o
            )
          );

          if (this.organisation$.value?.id === patchedOrga.id) {
            this.organisation$.next({
              ...this.organisation$.value,
              ...patchedOrga,
            });
          }
        })
      );
  }

  listAllOrganisations() {
    return this.http
      .get<{ data: Organisation[] }>(`${environment.userBackend}/admin/clients`)
      .pipe(
        map((data) => data.data),
        tap((orgs) => {
          this.organisations$.next(orgs);
        })
      );
  }

  deleteMyOrganisation() {
    return this.http
      .delete<{ data: Organisation }>(`${environment.userBackend}/clients`)
      .pipe(
        map((data) => data.data),
        tap((org) => {
          this.authService.logout();
        })
      );
  }

  deleteOrganisation(client_id: string) {
    return this.http
      .delete<{ data: Organisation }>(
        `${environment.userBackend}/admin/clients/${client_id}`
      )
      .pipe(
        map((data) => data.data),
        tap((org) => {
          console.log(org);
        })
      );
  }

  getClientTokenUsage(
    clientId: string,
    options: {
      period?: string;
      start_date?: string;
      end_date?: string;
      breakdown_limit?: number;
    } = {}
  ) {
    const params: any = {};
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });

    return this.http
      .get<{ data: ClientTokenUsageResponse }>(
        `${environment.userBackend}/admin/clients/${clientId}/token-usage`,
        { params }
      )
      .pipe(map((data) => data.data));
  }

  scheduleTokenUsageSync(payload: {
    days?: number;
    start_date?: string;
    end_date?: string;
    project_name?: string;
  } = {}) {
    return this.http
      .post<{ data: { status: string } }>(
        `${environment.userBackend}/admin/clients/token-usage/sync`,
        payload
      )
      .pipe(map((data) => data.data));
  }

  scheduleClientTokenUsageSync(
    clientId: string,
    payload: {
      days?: number;
      start_date?: string;
      end_date?: string;
      project_name?: string;
    } = {}
  ) {
    return this.http
      .post<{ data: { status: string; client_id: string } }>(
        `${environment.userBackend}/admin/clients/${clientId}/token-usage/sync`,
        payload
      )
      .pipe(map((data) => data.data));
  }

  getClientCredits(clientId: string) {
    return this.http
      .get<{ data: AdminClientCredits }>(`${environment.userBackend}/admin/clients/${clientId}/credits`)
      .pipe(map((data) => data.data));
  }

  grantClientCredits(clientId: string, payload: AdminCreditGiftPayload) {
    return this.http
      .post<{ data: { created: boolean; mail_sent: boolean; purchase_id: string | null; credits: AdminClientCredits } }>(
        `${environment.userBackend}/admin/clients/${clientId}/credits/grant`,
        payload
      )
      .pipe(map((data) => data.data));
  }

  getClientQuota(clientId: string) {
    return this.http
      .get<{ data: ClientQuotaDetails }>(
        `${environment.userBackend}/admin/clients/${clientId}/quota`
      )
      .pipe(map((data) => data.data));
  }

  updateClientQuota(
    clientId: string,
    payload: {
      api_usage_id?: string;
      token_quota_limit: number;
      note?: string;
    }
  ) {
    return this.http
      .patch<{ data: ClientQuotaDetails }>(
        `${environment.userBackend}/admin/clients/${clientId}/quota`,
        payload
      )
      .pipe(map((data) => data.data));
  }

  getPaymentLink(planId: string, interval: 'month' | 'year' = 'month') {
    const params: string[] = [];
    if (!environment.production) params.push('test_mode=true');
    if (interval === 'year') params.push('interval=year');
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http
      .post<{ data: any }>(
        `${environment.userBackend}/clients/plans/${planId}/payment-link${query}`,
        null,
        // The product card answers a refusal itself (billing-details dialog
        // or a plain hint) — no generic "400 Error" toast on top.
        { headers: { 'X-Suppress-Error-Toast': '1' } }
      )
      .pipe(
        map((data) => data.data),
        tap((payment) => {})
      );
  }

  /** Conditions of an in-portal upgrade. `available: false` = not offered
   *  this way; the caller falls back to the payment link (checkout / Stripe
   *  customer portal). */
  previewPlanChange(planId: string, interval: 'month' | 'year' = 'month') {
    const params: string[] = [];
    if (!environment.production) params.push('test_mode=true');
    if (interval === 'year') params.push('interval=year');
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http
      .get<{ data: PlanChangePreview }>(
        `${environment.userBackend}/clients/change-plan/${planId}/preview${query}`,
        { headers: { 'X-Suppress-Error-Toast': '1' } }
      )
      .pipe(map((data) => data.data));
  }

  /** Executes the upgrade shown by `previewPlanChange`: the difference is
   *  charged immediately, HTTP 402 when that payment fails. */
  changePlan(planId: string, prorationDate?: number) {
    const params: string[] = [];
    if (!environment.production) params.push('test_mode=true');
    // Pins the charge to the amount the preview showed.
    if (prorationDate) params.push(`proration_date=${prorationDate}`);
    return this.http.post(
      `${environment.userBackend}/clients/change-plan/${planId}${
        params.length ? `?${params.join('&')}` : ''
      }`,
      {},
      // The upgrade dialog shows the reason inline.
      { headers: { 'X-Suppress-Error-Toast': '1' } }
    );
  }

  getInvoices() {
    return this.http
      .get<{ data: any }>(
        `${environment.userBackend}/clients/invoices${
          environment.production ? '' : '?test_mode=true'
        }`
      )
      .pipe(
        map((data) => data.data),
        tap((invoices) => {
          this.invoices$.next(invoices);
        })
      );
  }

  getPaymentMethod() {
    return this.http
      .get<{ data: PaymentMethodInfo }>(
        `${environment.userBackend}/clients/payment-method${
          environment.production ? '' : '?test_mode=true'
        }`
      )
      .pipe(map((data) => data.data));
  }

  /** Stripe customer portal (card, invoices, cancel) — returns the session URL. */
  openBillingPortal() {
    return this.http
      .post<{ data: { url: string } }>(
        `${environment.userBackend}/clients/billing-portal${
          environment.production ? '' : '?test_mode=true'
        }`,
        {}
      )
      .pipe(map((data) => data.data.url));
  }

  getAvvStatus() {
    return this.http
      .get<{ data: AvvStatus }>(`${environment.userBackend}/clients/avv`)
      .pipe(map((data) => data.data));
  }

  acceptAvv(payload: AvvAcceptPayload) {
    return this.http
      .post<{ data: AvvStatus }>(
        `${environment.userBackend}/clients/avv/accept`,
        payload
      )
      .pipe(map((data) => data.data));
  }

  downloadAvv() {
    return this.http.get(`${environment.userBackend}/clients/avv/download`, {
      responseType: 'blob',
    });
  }

  startTrialPlan() {
    return this.http
      .post<{ data: any }>(
        `${environment.userBackend}/clients/start_trial${
          environment.production ? '' : '?test_mode=true'
        }`,
        null
      )
      .pipe(
        map((data) => data.data),
        tap((org) => {
          console.log(org);
        })
      );
  }

  extendTrial(data: { end_date: string; client_id: string }) {
    return this.http
      .post<{ data: any }>(
        `${environment.userBackend}/admin/extend_trial`,
        data
      )
      .pipe(
        tap((org) => {
          console.log(org);
        })
      );
  }

  get organisation() {
    return this.organisation$.value;
  }

  hasFeature(feature: string): boolean {
    if (!this.organisation) {
      return false;
    }
    return this.organisation.features.includes(feature);
  }

  // ---------------------------------------------------------------------
  // Super-Admin: per-client template-access whitelist
  // ---------------------------------------------------------------------
  // Backend lives in al-tutor (`tutorsBackend`) because that's where the
  // bot_templates / client_bot_templates_access tables sit. Both
  // endpoints are restricted to super_admin role server-side; calling
  // them as anyone else returns 403.

  listClientTemplateAccess(client_id: string) {
    return this.http
      .get<{ data: ClientTemplateAccess[] }>(
        `${environment.tutorsBackend}/admin/clients/${client_id}/template-access`
      )
      .pipe(map((res) => res.data));
  }

  /** Atomic replace of the whitelist: pass the full set of template
   *  ids the client should have access to. Public templates are
   *  silently dropped server-side (always visible anyway). */
  replaceClientTemplateAccess(client_id: string, template_ids: string[]) {
    return this.http.put<{ status: string; granted_template_ids: string[] }>(
      `${environment.tutorsBackend}/admin/clients/${client_id}/template-access`,
      { template_ids }
    );
  }

  private resetState() {
    this.organisation$.next(undefined);
    this.organisations$.next([]);
    this.invoices$.next([]);
  }
}
